import CustomModal from '@components/CustomModal';
import AddProcessForm from '@components/drawers/AddProcessForm';
import AddTriggerForm from '@components/drawers/AddTriggerForm';
import CustomDrawer from '@components/drawers/CustomDrawer';
import ImportExportProcessHandler from '@components/ImportExportHandler';
import RequestExecutionModal, { RequestExecutionConfirmData } from '@components/modals/RequestExecutionModal';
import SwitchOrganizationModal from '@components/modals/SwitchOrganizationModal';
import ProcessContent, { ProcessContentRef } from '@components/ProcessContent';
import ProcessContentStatic from '@components/ProcessContentStatic';
import ProcessExecutionsTable from '@components/ProcessExecutionsTable';
import ProcessHeader, { ProcessHeaderRef } from '@components/ProcessHeader';
import ProcessSidebar from '@components/ProcessSidebar';
import ProcessVersionHistory from '@components/ProcessVersionHistory';
import WolfButton from '@components/ui/WolfButton';
import { graphQlClient } from '@config/graphqlClient';
import DiscrepanciesGraphQL from '@graphql/discrepancy.queries';
import OrganizationsGraphQL from '@graphql/organization.queries';
import ProcessesGraphQL from '@graphql/process.queries';
import ProcessExecutionGraphQL from '@graphql/processExecution.queries';
import StepsGraphQL from '@graphql/step.queries';
import TriggersGraphQL from '@graphql/trigger.queries';
import UserGraphQL from '@graphql/user.queries';
import { useCrossOrgProcessCheck } from '@hooks/useCrossOrgProcessCheck';
import { useLoader } from '@hooks/useLoader';
import { useLocationQuery } from '@hooks/useLocationQuery';
import { useProcessExecution } from '@hooks/useProcessExecution';
import { useTranslation } from '@hooks/useTranslation';
import { Area } from '@models/area.model';
import { DrawerType, SnackType } from '@models/common.model';
import { getActiveDiscrepanciesAndPendingCountRequest } from '@models/discrepancy.model';
import { createOrUpdateOrganizationRequest } from '@models/organization.model';
import {
  createOrUpdateProcessRequest,
  deleteProcessRequest,
  getProcessByIdWithDiscrepanciesRequest,
  Process,
  processPolicyLinkRequest,
  ProcessStatus
} from '@models/process.model';
import { ProcessExecutionStatus } from '@models/processExecution.model';
import { Service } from '@models/service.model';
import { getAssignableUsersRequest, User } from '@models/user.model';
import {
  ConfirmationType,
  createOrUpdateStepRequest,
  deleteStepRequest,
  reorderStepsRequest,
  Step,
  StepType,
  TimingType
} from '@models/step.model';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setDiscrepancyCount } from '@redux/reducers/badges.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { setWolfieRefreshSeparation, setWolfieShouldRefreshData } from '@redux/reducers/wolfie.reducer';
import { RootState } from '@redux/store';
import trackUserActivity, { ActivityType } from '@utils/ai/trackUserActivity';
import { addStepToIds, addStepToIdsAtStart, cleanStepIds, removeStepFromIds } from '@utils/dragDropHelpers';
import { filterStepsInsideSwitchRepeat } from '@utils/filterStepsInsideSwitchRepeat';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';

const ProcessDescription: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { setLoading } = useLoader();

  const localeProcesses = useTranslation('processes');
  const localeProcessForm = useTranslation('processForm')
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');

  // const { shouldRefreshData: shouldRefreshDataWolfie, refreshSeparation: wolfieRefreshSeparation } = useAppSelector(
  //   (state: RootState) => state.wolfie,
  // );
  const { organizationId, onboardingDetails } = useAppSelector((state: RootState) => state.organization);
  const { rememberMe } = useAppSelector((state: RootState) => state.user);
  const { enabled: wolfieEnabled } = useAppSelector((state: RootState) => state.wolfie);

  const [currentProcess, setCurrentProcess] = useState<Process>();
  const [debouncedSteps, setDebouncedSteps] = useState<Step[]>([]);
  const [debounceSeparation, setDebounceSeparation] = useState(0);
  const [selectedService, setSelectedService] = useState<Partial<Service>>();
  const [selectedArea, setSelectedArea] = useState<Area>();
  const [stepName, setStepName] = useState('');
  const [linkedProcess, setLinkedProcess] = useState({ processId: '', name: '' });
  const [notes, setNotes] = useState('');
  const [condition, setCondition] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStep, setSelectedStep] = useState('');
  const [selectedStepData, setSelectedStepData] = useState<any>();
  const [stepType, setStepType] = useState<StepType | null>(StepType.PLAIN_TEXT);
  const [timingDescription, setTimingDescription] = useState('');
  const [timingName, setTimingName] = useState('');
  const [timingType, setTimingType] = useState(TimingType.IMMEDIATE);
  const [creationContext, setCreationContext] = useState<{ id: string; stepsIds: string; name: string }>();
  const [newStepType, setNewStepType] = useState<StepType | null>(null);
  const [updatingStep, setUpdatingStep] = useState<Partial<Step>>();
  const [originalStepData, setOriginalStepData] = useState<Partial<Step>>();
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [positionForNewStep, setPositionForNewStep] = useState<number | null>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showTriggerDrawer, setShowTriggerDrawer] = useState(false);
  const stepsRefs = useRef<{ [key: string]: any | null }>({});
  const processContentRef = useRef<ProcessContentRef>(null);
  const processHeaderRef = useRef<ProcessHeaderRef>(null);
  const [showAddStepConfirmDialog, setShowAddStepConfirmDialog] = useState(false);
  const [pendingNewStep, setPendingNewStep] = useState<{
    item: { text: string; value: string };
    position?: number;
    context?: { id: string; stepsIds: string; name: string };
  } | null>(null);
  const [pendingStepSelection, setPendingStepSelection] = useState<string | null>(null);
  const [showJsonImport, setShowJsonImport] = useState(false);
  const [showJsonExport, setShowJsonExport] = useState(false);
  const [showDeleteProcessModal, setShowDeleteProcessModal] = useState(false);
  const [showExecutionsTable, setShowExecutionsTable] = useState(false);
  const [showSwitchOrgModal, setShowSwitchOrgModal] = useState(false);
  const [processNotFound, setProcessNotFound] = useState(false);
  const [crossOrgResult, setCrossOrgResult] = useState<{
    organizationName: string;
    organizationId: string;
  } | null>(null);

  const executeCheckRef = useRef(false); // Guard to prevent multiple execution checks

  // Request execution state
  const [showRequestExecutionModal, setShowRequestExecutionModal] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);

  const { processId } = useParams<{ processId: string }>();
  const query = useLocationQuery();

  const { checkProcessAcrossOrganizations, switchToProcessOrganization } = useCrossOrgProcessCheck();

  // Process execution hook
  const {
    isExecuting,
    stepExecutions,
    currentStepIndex,
    showStatusModal,
    isGoingBack,
    substepExecutions,
    currentSubstepIndices,
    repeatIterations,
    completedIterations,
    showIterationCompleteFor,
    showContinueExecutionModal,
    pendingExecutionStartDate,
    showAssignerNoteModal,
    assignerNoteContent,
    noteModalNextAction,
    setShowStatusModal,
    setShowContinueExecutionModal,
    setShowAssignerNoteModal,
    setAssignerNoteContent,
    setPendingExecutionStartDate,
    setPendingExecutionId,
    setNoteModalNextAction,
    checkAndShowExecutionConfirm,
    handleStartExecution,
    handleMarkStep,
    handleCompleteExecution,
    handleCancelExecution,
    handleGoBack,
    handleMarkSubstep,
    handleRepeatAddAnother,
    handleRepeatDone,
  } = useProcessExecution({
    currentProcess,
    processId,
    stepsRefs,
    processHeaderRef,
    localeProcesses,
    localeActionMessages,
  });

  const createNewStep = (order: number, isCondition: boolean = false): Step => ({
    stepId: 'in-progress-' + order,
    name: isCondition ? localeProcesses['aNewCondition'] : localeProcesses['aNewStep'],
    stepType: StepType.PLAIN_TEXT,
    processId: processId,
    notes: '',
    order: order,
  });

  // Helper to remove in-progress steps from the process
  const removeInProgressSteps = () => {
    if (currentProcess?.steps?.some((step) => isInProgressStep(step?.stepId))) {
      setCurrentProcess((prevCurr) => {
        if (!prevCurr || !prevCurr.steps) return prevCurr;
        return {
          ...prevCurr,
          steps: prevCurr.steps.filter((step) => !isInProgressStep(step?.stepId)),
        };
      });
    }
  };

  const isDescriptionEmpty = (description: string): boolean => {
    if (!description || description.trim() === '') {
      return true;
    }
    // Check if description is just an empty list item (with various whitespace/nbsp variations)
    const trimmedDescription = description.trim();
    return (
      trimmedDescription === '<ul><li></li></ul>' ||
      trimmedDescription === '<ul>\n<li>&nbsp;</li>\n</ul>' ||
      // Also handle variations with different whitespace
      /^<ul>\s*<li>(&nbsp;|\s)*<\/li>\s*<\/ul>$/.test(trimmedDescription)
    );
  };

  const normalizeDescription = (description: string): string => {
    return isDescriptionEmpty(description) ? '' : description;
  };

  const hasUnsavedChanges = (): boolean => {
    if (!updatingStep || !originalStepData) {
      return false;
    }

    // Normalize descriptions for comparison
    const currentDesc = normalizeDescription(updatingStep.stepData?.description || '');
    const originalDesc = normalizeDescription(originalStepData.stepData?.description || '');

    // Check if any field has changed from the original
    return (
      updatingStep.name !== originalStepData.name ||
      updatingStep.notes !== originalStepData.notes ||
      currentDesc !== originalDesc ||
      updatingStep.stepData?.condition !== originalStepData.stepData?.condition ||
      updatingStep.stepData?.executeProcessId !== originalStepData.stepData?.executeProcessId ||
      updatingStep.stepData?.timingType !== originalStepData.stepData?.timingType ||
      updatingStep.stepData?.timingName !== originalStepData.stepData?.timingName ||
      updatingStep.stepData?.timingDescription !== originalStepData.stepData?.timingDescription
    );
  };

  // Helper function to check if a step is in progress
  const isInProgressStep = (stepId: string): boolean => {
    return stepId.startsWith('in-progress-');
  };


  useEffect(() => {
    setLoading(true);
    getProcessData(processId);
    executeCheckRef.current = false; // Reset guard when navigating to a different process
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId]);

  useEffect(() => {
    const stepId = query.get('stepId');
    if (stepId) {
      handleSelectStep(stepId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Check for execute parameter to show confirmation dialog
  useEffect(() => {
    const shouldExecute = query.get('execute');
    if (shouldExecute === 'true' && currentProcess && !executeCheckRef.current) {
      executeCheckRef.current = true; // Mark as processed to prevent multiple calls
      // Remove the query parameter from URL first
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      // Check for pending execution and show modal
      checkAndShowExecutionConfirm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, currentProcess]);


  useEffect(() => {
    if (currentProcess?.steps && !selectedStep) {
      // Filter out substeps - only select top-level steps
      const allSteps = currentProcess.steps;
      const topLevelSteps = allSteps.filter((step) => {
        return !allSteps.some((s) => {
          if (s.stepData?.stepsIds && typeof s.stepData.stepsIds === 'string') {
            return s.stepData.stepsIds.split(',').filter((id: string) => id && id !== 'undefined').includes(step.stepId);
          }
          return false;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProcess]);

  useEffect(() => {
    if (debouncedSteps && debouncedSteps.length > 0) {
      const step = { ...debouncedSteps[0] };
      if (step) {
        setTimeout(() => {
          setCurrentProcess((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              steps: [...(prev.steps?.filter((s) => s.stepId !== step.stepId) || []), step],
            };
          });
          setDebouncedSteps(debouncedSteps.slice(1));
        }, debounceSeparation);
      }
    } else {
      setDebounceSeparation(0);
      dispatch(setWolfieRefreshSeparation(0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSteps]);

  // useEffect(() => {
  //   if (shouldRefreshDataWolfie) {
  //     if (wolfieRefreshSeparation) {
  //       getProcessData(processId, wolfieRefreshSeparation);
  //     } else {
  //       getProcessData(processId);
  //     }
  //     dispatch(setWolfieShouldRefreshData(false));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [shouldRefreshDataWolfie]);

  const getProcessData = async (id: string, separation?: number, preserveEditMode?: boolean) => {
    try {
      if (!id) return;
      const variables = { id };
      const data: getProcessByIdWithDiscrepanciesRequest = await graphQlClient.request(
        ProcessesGraphQL.queries.getProcessByIdWithDiscrepancies,
        variables,
      );
      if (data?.getProcessByIdWithDiscrepancies) {
        // @ts-ignore
        setDebouncedSteps(data?.getProcessByIdWithDiscrepancies?.steps || []);
        setDebounceSeparation(separation || 0);
        setCurrentProcess(data?.getProcessByIdWithDiscrepancies);

        setSelectedService(data?.getProcessByIdWithDiscrepancies?.service);
        setSelectedArea(data?.getProcessByIdWithDiscrepancies?.service?.area);
        if (data?.getProcessByIdWithDiscrepancies?.steps?.length === 0) {
          const newStep: Step = createNewStep(0);
          const processWithNewStep = {
            ...data?.getProcessByIdWithDiscrepancies,
            steps: data?.getProcessByIdWithDiscrepancies?.steps
              ? [...data?.getProcessByIdWithDiscrepancies?.steps, newStep]
              : [newStep],
          };
          // @ts-ignore
          setCurrentProcess(processWithNewStep);
          setSelectedStep(newStep.stepId);
          setStepName(newStep.name);
          setUpdatingStep({ name: newStep.name });
          setEditMode(true);
        } else if (!preserveEditMode) {
          setEditMode(false);
          // Clear invalid selectedStep (in-progress steps don't exist in server data)
          // But don't clear if we're in the middle of adding a new step
        }
      }
    } catch (e: any) {
      console.log('error', e);
      console.log('error message', e?.message);
      // If process not found (404), check other organizations
      if (e?.message?.includes('Could not find any entity') || e?.response?.status === 404) {
        const result = await checkProcessAcrossOrganizations(id);
        if (result) {
          setCrossOrgResult({
            organizationName: result.organizationName,
            organizationId: result.organizationId,
          });
          setShowSwitchOrgModal(true);
        } else {
          // Process not found in any organization
          setProcessNotFound(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (
    addNewAfter: boolean = false,
    item?: { text: string; value: string },
    position?: number,
    context?: { id: string; stepsIds: string; name: string },
  ) => {
    setSaving(true);
    if (!updatingStep?.name) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['stepNameRequired'],
          type: SnackType.WARNING,
        }),
      );
      setSaving(false);
      return;
    }
    const isEdit = !!selectedStep && !isInProgressStep(selectedStep);
    try {
      const query = isEdit ? StepsGraphQL.mutations.updateStep : StepsGraphQL.mutations.createStep;
      const auxStep = currentProcess?.steps?.find((step) => step?.stepId === selectedStep);
      const params: { step: Partial<Step> } = {
        step: {
          stepId: (!isInProgressStep(selectedStep) ? selectedStep : undefined) || undefined,
          name: updatingStep?.name,
          stepType: newStepType || StepType.PLAIN_TEXT,
          notes: updatingStep?.notes || '',
          order: positionForNewStep || undefined,
          stepData: {
            description: normalizeDescription(updatingStep?.stepData?.description || '') || null,
            condition: updatingStep?.stepData?.condition,
            executeProcessId: updatingStep?.stepData?.executeProcessId,
            timingType: updatingStep?.stepData?.timingType,
            timingName: updatingStep?.stepData?.timingName,
            timingDescription: updatingStep?.stepData?.timingDescription,
            stepsIds: isEdit ? auxStep?.stepData?.stepsIds : null,
            confirmationType: updatingStep?.stepData?.confirmationType || ConfirmationType.NONE,
            confirmationQuestion: updatingStep?.stepData?.confirmationQuestion || undefined,  
          },
          processId: processId,
        },
      };
      if (!isEdit) {
        delete params.step.stepId;
      } else {
        delete params.step.stepType;
        delete params.step.order;
      }
      const data: createOrUpdateStepRequest = await graphQlClient.request(query, params);
      if (data?.createStep?.stepId || data?.updateStep?.stepId) {
        trackUserActivity(ActivityType.PROCESS_UPDATE, rememberMe, processId);

        // Get the step and add the new step to the stepIds of the context if we have it (because it is inside a switch or repeat)
        if (creationContext?.id) {
          try {
            // IMPORTANT: Fetch the CURRENT stepsIds from the container step, not the stale creationContext
            // The container's stepsIds may have changed since creationContext was set
            const containerStep = currentProcess?.steps?.find((s: Step) => s.stepId === creationContext.id);
            const currentStepsIds = containerStep?.stepData?.stepsIds || creationContext?.stepsIds || '';

            // Clean the existing stepsIds (remove in-progress and invalid entries) and add new step
            const cleanedExisting = cleanStepIds(currentStepsIds.replace(/in-progress-\d+/g, ''));
            const newStepId = data?.createStep?.stepId || '';
            const updatedIds = addStepToIds(cleanedExisting, newStepId);
            await graphQlClient.request(StepsGraphQL.mutations.updateStepsIds, {
              step: {
                stepId: creationContext.id,
                stepData: {
                  name: containerStep?.name || creationContext?.name,
                  stepsIds: updatedIds,
                },
              },
            });
          } catch (e: any) {
            showErrorSnack(e);
          }
        }
        if (!addNewAfter) {
          // Immediately update currentProcess with the saved step (remove in-progress step)
          setCurrentProcess((prevCurr) => {
            if (!prevCurr) return prevCurr;
            return {
              ...prevCurr,
              steps: [
                ...(prevCurr?.steps?.filter((item) => item.stepId !== selectedStep) || []),
                isEdit ? data?.updateStep : data?.createStep,
              ] as Step[],
            };
          });
          getProcessData(processId);
          setEditMode(false);
          clearStepData();
        } else {
          setCurrentProcess((prevCurr) => {
            if (!prevCurr) return prevCurr;

            if (creationContext?.id) {
              const contextStep = prevCurr.steps?.find((step) => step?.stepId === creationContext?.id);
              if (contextStep) {
                // Clean existing stepsIds (remove in-progress entries) before adding new step
                const cleanedStepsIds = cleanStepIds(contextStep.stepData?.stepsIds?.replace(/in-progress-\d+/g, ''));
                const updatedContextStep = {
                  ...contextStep,
                  stepData: {
                    ...contextStep.stepData,
                    stepsIds: addStepToIds(cleanedStepsIds, data?.createStep?.stepId || ''),
                  },
                };
                const newSteps = [
                  ...(prevCurr.steps?.filter(
                    (item) => item.stepId !== selectedStep && item.stepId !== creationContext?.id,
                  ) || []),
                  updatedContextStep,
                  isEdit ? data?.updateStep : data?.createStep,
                ] as Step[];
                return { ...prevCurr, steps: newSteps };
              }
            }

            return {
              ...prevCurr,
              steps: [
                ...(prevCurr.steps?.filter((item) => item.stepId !== selectedStep) || []),
                isEdit ? data?.updateStep : data?.createStep,
              ] as Step[],
            };
          });
        }
        dispatch(
          appendActionMessage({
            message: isEdit
              ? localeActionMessages['stepUpdatedSuccessfully']
              : localeActionMessages['stepCreatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
        setCreationContext(undefined);
        if (addNewAfter && item) {
          auxAddNewStep(item, position, context);
        }
      }
    } catch (e: any) {
      showErrorSnack(e);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    clearStepData();
    setCreationContext(undefined);
    removeInProgressSteps();
  };

  const showErrorSnack = (e: any) => {
    setSaving(false);
    dispatch(
      appendActionMessage({
        message: e?.response?.errors[0]?.message || localeCommon['requestError'],
        type: SnackType.ERROR,
      }),
    );
  };

  const handleEditStep = (id: string) => {
    if (editMode && selectedStep === id) {
      setEditMode(false);
      clearStepData();
      setCreationContext(undefined);
      removeInProgressSteps();
      return;
    }
    handleSelectStep(id || currentProcess?.steps?.sort((a, b) => (a.order > b.order ? 1 : -1))[0]?.stepId || '');
    setEditMode(true);

    // Initialize all step data when entering edit mode
    // We must set this here because handleSelectStep may return early if the step is already selected
    const auxStep = currentProcess?.steps?.find((step: Step) => step?.stepId === id);
    if (auxStep) {
      // Set form state (props for ProcessContent)
      const stepName = auxStep.name || '';
      const stepNotes = auxStep.notes || '';
      const stepCondition = auxStep.stepData?.condition || '';
      const stepDescription = normalizeDescription(auxStep.stepData?.description || '');
      const stepExecuteProcessId = auxStep.stepData?.executeProcessId || '';
      const stepTimingType = auxStep.stepData?.timingType || TimingType.IMMEDIATE;
      const stepTimingName = auxStep.stepData?.timingName || '';
      const stepTimingDescription = auxStep.stepData?.timingDescription || '';

      setStepName(stepName);
      setNotes(stepNotes);
      setCondition(stepCondition);
      setDescription(stepDescription);
      setLinkedProcess({
        processId: stepExecuteProcessId,
        name: '',
      });
      setTimingType(stepTimingType);
      setTimingName(stepTimingName);
      setTimingDescription(stepTimingDescription);
      setSelectedStepData(auxStep.stepData);

      // Set both updatingStep and originalStepData with identical normalized values
      // IMPORTANT: Create separate objects to avoid shared reference mutation
      setUpdatingStep({
        name: stepName,
        notes: stepNotes,
        stepData: {
          description: stepDescription,
          condition: stepCondition,
          executeProcessId: stepExecuteProcessId,
          confirmationType: auxStep.stepData?.confirmationType || ConfirmationType.NONE,
          confirmationQuestion: auxStep.stepData?.confirmationQuestion || '',
          timingType: stepTimingType,
          timingName: stepTimingName,
          timingDescription: stepTimingDescription,
        },
      });
      setOriginalStepData({
        name: stepName,
        notes: stepNotes,
        stepData: {
          description: stepDescription,
          condition: stepCondition,
          executeProcessId: stepExecuteProcessId,
          confirmationType: auxStep.stepData?.confirmationType || ConfirmationType.NONE,
          confirmationQuestion: auxStep.stepData?.confirmationQuestion || '',
          timingType: stepTimingType,
          timingName: stepTimingName,
          timingDescription: stepTimingDescription,
        },
      });
    }
  };

  const toggleEdit = () => {
    setShowDrawer(true);
  };

  const toggleEditTriggers = () => {
    setShowTriggerDrawer(true);
  };

  const handleDelete = async () => {
    setSaving(true);
    if (isInProgressStep(selectedStep)) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['youMustFirstSaveTheStep'],
          type: SnackType.WARNING,
        }),
      );
      setSaving(false);
      return;
    }
    const savedSteps = currentProcess?.steps?.filter((s) => !isInProgressStep(s.stepId)) || [];
    if (savedSteps.length <= 1) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['cannotDeleteOnlyStep'],
          type: SnackType.WARNING,
        }),
      );
      setSaving(false);
      return;
    }
    if (selectedStep) {
      try {
        const deleteResult: deleteStepRequest = await graphQlClient.request(StepsGraphQL.mutations.deleteStep, {
          id: selectedStep,
        });
        if (deleteResult?.deleteStep?.affected > 0) {
          trackUserActivity(ActivityType.STEP_DELETION, rememberMe, selectedStep, processId);
          getProcessData(processId);
          dispatch(
            appendActionMessage({ message: localeActionMessages['stepDeletedSuccessfully'], type: SnackType.SUCCESS }),
          );
          clearStepData();
          setCreationContext(undefined);
        }
      } catch (e: any) {
        showErrorSnack(e);
      } finally {
        setSaving(false);
      }
    }
  };

  const addNewStep = (
    item: { text: string; value: string },
    position?: number,
    context?: { id: string; stepsIds: string; name: string },
  ) => {
    // If in edit mode, always prompt user to save/discard/continue
    if (editMode) {
      setPendingNewStep({ item, position, context });
      setShowAddStepConfirmDialog(true);
      return;
    }

    auxAddNewStep(item, position, context);
  };

  const handleSaveAndAddNew = async () => {
    if (pendingStepSelection) {
      // Save current step and select the pending step
      const stepToSelect = pendingStepSelection;
      setPendingStepSelection(null);
      setShowAddStepConfirmDialog(false);
      await handleSave(false);
      setSelectedStep(stepToSelect);
      return;
    }
    if (!pendingNewStep) return;
    const stepToAdd = { ...pendingNewStep };
    setPendingNewStep(null);
    setShowAddStepConfirmDialog(false);
    // Use the original flow which saves and adds in sequence
    handleSave(true, stepToAdd.item, stepToAdd.position, stepToAdd.context);
    // Reset flag after a delay to allow the save+add flow to complete
  };

  const handleDiscardAndAddNew = () => {
    if (pendingStepSelection) {
      // Discard changes and select the pending step
      const stepToSelect = pendingStepSelection;
      setPendingStepSelection(null);
      setShowAddStepConfirmDialog(false);
      setEditMode(false);
      clearStepData();
      setCreationContext(undefined);
      removeInProgressSteps();
      setSelectedStep(stepToSelect);
      return;
    }
    if (!pendingNewStep) return;
    const stepToAdd = { ...pendingNewStep };
    setPendingNewStep(null);
    setShowAddStepConfirmDialog(false);
    setEditMode(false);
    clearStepData();
    setCreationContext(undefined);
    removeInProgressSteps();
    // Small delay to allow React state to update before adding new step
    setTimeout(() => {
      auxAddNewStep(stepToAdd.item, stepToAdd.position, stepToAdd.context);
    }, 300);
  };

  const handleContinueEditing = () => {
    setShowAddStepConfirmDialog(false);
    setPendingNewStep(null);
    setPendingStepSelection(null);
  };

  const auxAddNewStep = (
    item: { text: string; value: string },
    position?: number,
    context?: { id: string; stepsIds: string; name: string },
  ) => {
    if (!currentProcess) {
      return;
    }
    // Clear any previous step data before creating a new step
    clearStepData();

    setEditMode(true);
    if (context) {
      setCreationContext(context);
    }
    const stepType = item.value as StepType;
    setStepType(null);
    setNewStepType(stepType);
    if (position) {
      setPositionForNewStep(position || null);
    }
    // Check if we're creating a step inside a SWITCH case (condition)
    const isConditionStep =
      context?.id && currentProcess?.steps?.find((step) => step.stepId === context.id)?.stepType === StepType.SWITCH;

    let newStepName = '';
    if (isConditionStep && context?.stepsIds) {
      // Generate condition name like "Condition a", "Condition b", etc.
      const conditionCount = context.stepsIds.split(',').filter((id) => id && id.trim() && id !== 'undefined').length;
      const conditionLetter = String.fromCharCode(97 + conditionCount); // 'a', 'b', 'c', etc.
      newStepName = `${localeProcesses['conditionPrefix']} ${conditionLetter}`;
    } else {
      newStepName =
        stepType === StepType.REPEAT
          ? localeProcesses['aNewRepeatStep']
          : stepType === StepType.SWITCH
            ? localeProcesses['aNewSwitchStep']
            : isConditionStep
              ? localeProcesses['aNewCondition']
              : localeProcesses['aNewStep'];
    }

    let auxOrder = 0;
    if (context?.id && context?.stepsIds) {
      auxOrder = position || 0;
      //currentProcess?.steps?.find((item) => item?.stepId === context?.stepsIds?.split(',')[position])?.order || 0;
    } else {
      auxOrder = position || 0;
      // (currentProcess?.steps && position !== undefined && currentProcess.steps[position]?.order !== undefined
      //   ? currentProcess.steps[position].order
      //   : position) || 0;
    }
    const newStep = {
      stepId: 'in-progress-' + auxOrder,
      name: newStepName,
      stepType: item.value as StepType,
      processId: processId,
      notes: '',
      order: auxOrder,
    };
    const index = position && position > 0 ? position : 0;
    if (!currentProcess?.steps) {
      currentProcess.steps = [];
    }
    if (context?.id && currentProcess?.steps) {
      setCurrentProcess((prevCurrentProcess) => {
        if (!prevCurrentProcess) return prevCurrentProcess;

        const includedIn = prevCurrentProcess.steps?.find((step) => step.stepId === context.id);
        if (!includedIn?.stepId) return prevCurrentProcess;

        // Clone the parent step and update its stepsIds
        const cleanedStepsIds = cleanStepIds(includedIn.stepData?.stepsIds?.replace(/in-progress-\d+/g, ''));
        const updatedIncludedIn = {
          ...includedIn,
          stepData: {
            ...includedIn.stepData,
            stepsIds: addStepToIds(cleanedStepsIds, 'in-progress-' + auxOrder),
          },
        };

        const updatedSteps = [
          ...(prevCurrentProcess.steps?.filter(
            (step: Step | undefined) =>
              step?.stepId !== context.id &&
              (step?.stepId ? !isInProgressStep(step.stepId) : true) &&
              step !== undefined,
          ) || []),
          updatedIncludedIn,
          newStep,
        ];
        return { ...prevCurrentProcess, steps: updatedSteps };
      });
    } else {
      setCurrentProcess((prevCurr) => {
        if (!prevCurr) return prevCurr;
        const updatedSteps = prevCurr.steps
          ? [...prevCurr.steps.slice(0, index), newStep, ...prevCurr.steps.slice(index)].filter(
              (step) => step !== undefined,
            )
          : [newStep];
        return { ...prevCurr, steps: updatedSteps };
      });
    }
    setSelectedStep('in-progress-' + auxOrder);
    setSelectedStepData({ name: newStepName });
    setStepName(newStepName);
    setUpdatingStep({ name: newStepName });

    // For new steps, set original data as empty so any changes are considered dirty
    setOriginalStepData({
      name: '',
      notes: '',
      stepData: {
        description: '',
        condition: '',
        executeProcessId: '',
        timingType: TimingType.IMMEDIATE,
        timingName: '',
        timingDescription: '',
      },
    });

    setSaving(false);

    // Focus the name field after the component updates
    setTimeout(() => {
      processContentRef.current?.focusNameField();
    }, 200);
  };

  // TODO: Now we also need to link the policy after saving the process
  const handleDrawerSave = async (process: Process, policies: { text: string; value: string }[]) => {
    if (!process.name || !process.goal || !process.serviceId) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['processNameGoalServiceRequired'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }
    setSaving(true);
    const isEdit = true; // Because we are editing the process
    if (isEdit && !process.processId) {
      return appendActionMessage({
        message: localeCommon['somethingWentWrong'],
        type: SnackType.ERROR,
      });
    }
    const auxPolicy = policies?.map((policy: { text: string; value: string }) => policy.value);
    try {
      if (isEdit) {
        //Remove policies, triggers, steps from the process object since they are updated separately
        delete process.policies;
        delete process.triggers;
        delete process.steps;
        delete process.service;
        delete process.location;
        delete process.triggeredBy;
        delete process.generatedFromName;
        delete process.generatedFrom;
        delete process.discrepancies;
        delete process.lastUpdatedAt;
        delete process.currentVersion;
      }
      const query =
        isEdit && process.processId
          ? ProcessesGraphQL.mutations.updateProcess
          : ProcessesGraphQL.mutations.createProcess;
      const params = {
        process: {
          ...process,
          serviceId: !isEdit ? selectedService?.serviceId || process.serviceId : process.serviceId,
        },
      };
      const data: createOrUpdateProcessRequest = await graphQlClient.request(query, params);
      if (data?.createProcess?.processId || data?.updateProcess?.processId) {
        if (auxPolicy) {
          await handleLinkProcessPolicies(process?.processId, auxPolicy);
        }

        // Always refresh process data to get updated currentVersion
        await getProcessData(processId);

        dispatch(
          appendActionMessage({
            message: isEdit
              ? localeActionMessages['updateSuccessful']
              : localeActionMessages['processCreatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
        setShowDrawer(false);
      }
    } catch (e: any) {
      showErrorSnack(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProcess = (id: string) => {
    // Only show confirmation modal if process has steps
    if (currentProcess?.steps && currentProcess.steps.length > 0) {
      setShowDeleteProcessModal(true);
    } else {
      confirmDeleteProcess(id);
    }
  };

  const confirmDeleteProcess = async (id?: string) => {
    const processIdToDelete = id || processId;
    try {
      const deleteResult: deleteProcessRequest = await graphQlClient.request(ProcessesGraphQL.mutations.deleteProcess, {
        id: processIdToDelete,
      });
      if (deleteResult?.deleteProcess?.affected > 0) {
        trackUserActivity(ActivityType.PROCESS_DELETION, rememberMe, processIdToDelete, selectedService?.serviceId);
        dispatch(appendActionMessage({ message: localeActionMessages['deleteSuccessful'], type: SnackType.SUCCESS }));
        setShowDrawer(false);
        setShowDeleteProcessModal(false);
        setCurrentProcess(undefined);

        setTimeout(() => {
          history.push('/processes');
        }, 50);
      }
    } catch (e: any) {
      showErrorSnack(e);
      setShowDeleteProcessModal(false);
    }
  };

  const cancelDeleteProcess = () => {
    setShowDeleteProcessModal(false);
  };

  const handleSelectStep = (stepId: string, fromScroll: boolean = false, _skipCheck: boolean = false) => {
    // TODO: Check if the skipCheck property is needed
    if (stepId === selectedStep) {
      return;
    }
    // Show confirmation dialog when selecting a different step while editing with unsaved changes
    if (editMode && (isInProgressStep(selectedStep) || hasUnsavedChanges())) {
      setPendingStepSelection(stepId);
      setShowAddStepConfirmDialog(true);
      return;
    }
    // Track if we're exiting edit mode - we'll skip setting originalStepData in that case
    const exitingEditMode = editMode;
    if (editMode) {
      setEditMode(false);
    }
    setNewStepType(null);
    setSelectedStep(stepId);
    if (!fromScroll) {
      // Add a small delay to ensure DOM is ready when ref doesn't exist
      if (stepsRefs.current[stepId]) {
        stepsRefs.current[stepId].scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        setTimeout(() => {
          stepsRefs.current[stepId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 1000);
      }
    }
    const auxStep = currentProcess?.steps?.find((step: Step) => step?.stepId === stepId);
    if (auxStep?.stepType === 'SWITCH' || auxStep?.stepType === 'REPEAT') {
      setCreationContext({
        id: auxStep?.stepId || '',
        stepsIds: auxStep.stepData?.stepsIds || '',
        name: auxStep.name || '',
      });
    } else {
      setCreationContext(undefined);
    }
    setStepName(auxStep?.name || '');
    setLinkedProcess({
      processId: auxStep?.stepData?.executeProcessId || '',
      name: '',
    });
    setNotes(auxStep?.notes || '');
    setStepType(auxStep?.stepType || StepType.PLAIN_TEXT);
    setCondition(auxStep?.stepData?.condition || '');
    setDescription(auxStep?.stepData?.description || '');
    setTimingDescription(auxStep?.stepData?.timingDescription || '');
    setTimingName(auxStep?.stepData?.timingName || '');
    setTimingType(auxStep?.stepData?.timingType || TimingType.IMMEDIATE);
    setSelectedStepData(auxStep?.stepData)

    // Store original step data for dirty checking
    // Skip this when exiting edit mode to avoid mismatch with stale updatingStep
    if (!exitingEditMode) {
      setOriginalStepData({
        name: auxStep?.name || '',
        notes: auxStep?.notes || '',
        stepData: {
          description: auxStep?.stepData?.description || '',
          condition: auxStep?.stepData?.condition || '',
          executeProcessId: auxStep?.stepData?.executeProcessId || '',
          confirmationType: auxStep?.stepData?.confirmationType || ConfirmationType.NONE,
          confirmationQuestion: auxStep?.stepData?.confirmationQuestion || '',
          timingType: auxStep?.stepData?.timingType || TimingType.IMMEDIATE,
          timingName: auxStep?.stepData?.timingName || '',
          timingDescription: auxStep?.stepData?.timingDescription || '',
        },
      });
    }
  };

  const handleStepDetailChange = (updated: any) => {
    if (Object.keys(updated).length === 0) {
      return;
    }
    setUpdatingStep((prevUpdatingStep) => {
      // Create a new object that only includes updated properties
      const newUpdatingStep = { ...prevUpdatingStep };

      if (updated?.name) {
        newUpdatingStep.name = updated.name;
        const inProgressStep = currentProcess?.steps?.find((step) => isInProgressStep(step?.stepId));
        if (inProgressStep && updated.name.length > 2) {
          inProgressStep.name = updated.name;
        } else if (inProgressStep) {
          inProgressStep.name = '...';
        }
      }
      if (updated.name === '') {
        newUpdatingStep.name = '';
      }
      if (updated?.notes || updated?.notes === '') {
        newUpdatingStep.notes = updated.notes;
      }
      if (updated?.description || updated?.description === '') {
        const normalizedDescription = normalizeDescription(updated?.description || '');
        newUpdatingStep.stepData = {
          ...newUpdatingStep.stepData,
          description: normalizedDescription,
        };
      }
      if (updated?.condition || updated?.condition === '') {
        newUpdatingStep.stepData = {
          ...newUpdatingStep.stepData,
          condition: updated?.condition || (updated?.condition === '' ? updated.condition : ''),
        };
      }
      if (updated?.stepData?.confirmationType !== undefined) {
        newUpdatingStep.stepData = {
          ...newUpdatingStep.stepData,
          confirmationType: updated.stepData.confirmationType,
        };
      }
      if (updated?.stepData?.confirmationQuestion !== undefined) {
        newUpdatingStep.stepData = {
          ...newUpdatingStep.stepData,
          confirmationQuestion: updated.stepData.confirmationQuestion,
        };
      }
      if (updated.linked?.processId || updated.linked?.processId === '') {
        newUpdatingStep.stepData = {
          ...newUpdatingStep.stepData,
          ...(updated?.linked && { executeProcessId: updated.linked.processId }),
        };
      }
      if (updated.timingType || updated.timingName || updated.timingDescription) {
        newUpdatingStep.stepData = {
          ...newUpdatingStep.stepData,
          ...(updated?.timingType && { timingType: updated.timingType }),
          ...(updated?.timingName && { timingName: updated.timingName }),
          ...(updated?.timingDescription && { timingDescription: updated.timingDescription }),
        };
        if (updated.timingName === '') {
          newUpdatingStep.stepData.timingName = '';
        }
        if (updated.timingDescription === '') {
          newUpdatingStep.stepData.timingDescription = '';
        }
      }

      return newUpdatingStep;
    });
  };

  const handleDragInsideOutside = async (moveInside: boolean, itemId: string, containerId: string, targetMainIndex?: number, targetSubstepIndex?: number) => {

    if (!itemId || !containerId || itemId === 'undefined' || containerId === 'undefined') {
      return;
    }

    // Prevent a step from being added to its own substeps
    if (moveInside && itemId === containerId) {
      dispatch(
        appendActionMessage({
          message: 'Cannot move a step inside itself',
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    setSaving(true);
    try {
      const containerStep = currentProcess?.steps?.find((step: Step) => step?.stepId === containerId);
      if (!containerStep) {
        throw new Error(`Container step not found: ${containerId}`);
      }

      const currentStepsIds = containerStep.stepData?.stepsIds || '';
      // Parse and clean the current IDs
      const currentIds = currentStepsIds
        .split(',')
        .map((id: string) => id.trim())
        .filter((id: string) => id && id !== 'undefined' && id !== 'null');

      let updatedIds: string;

      if (moveInside) {
        // Validate itemId before adding
        if (!itemId || itemId === 'undefined' || itemId === 'null') {
          console.error('[handleDragInsideOutside] Attempted to add invalid itemId:', itemId);
          setSaving(false);
          return;
        }

        // Check if already exists
        if (currentIds.includes(itemId)) {
          console.warn('[handleDragInsideOutside] itemId already in container:', itemId);
          updatedIds = currentIds.join(',');
        } else if (targetSubstepIndex !== undefined && targetSubstepIndex >= 0) {
          // Insert at specific position
          currentIds.splice(targetSubstepIndex, 0, itemId);
          updatedIds = currentIds.join(',');
        } else {
          // Add at start
          updatedIds = [itemId, ...currentIds].join(',');
        }
      } else {
        // Remove from container
        updatedIds = currentIds.filter((id: string) => id !== itemId).join(',');
      }

      // Final validation before sending to backend
      const finalIds = updatedIds
        .split(',')
        .filter(id => id && id !== 'undefined' && id !== 'null')
        .join(',');

      if (finalIds !== updatedIds) {
        updatedIds = finalIds;
      }

      await graphQlClient.request(StepsGraphQL.mutations.updateStepsIds, {
        step: {
          stepId: containerId,
          stepData: {
            name: containerStep.name,
            stepsIds: updatedIds,
          },
        },
      });

      // If moving inside, also reorder to ensure the step's order field is set correctly
      if (moveInside && updatedIds) {
        const stepsIdsArray = updatedIds.split(',').filter(id => id && id.trim() && id !== 'undefined' && id !== 'null');
        if (stepsIdsArray.length > 0) {
          console.log('[handleDragInsideOutside] Reordering steps inside container:', stepsIdsArray);
          await graphQlClient.request(StepsGraphQL.mutations.reorderSteps, {
            steps: { stepsIds: stepsIdsArray },
          });
        }
      }

      // If moving outside with a target position, reorder main steps
      if (!moveInside && targetMainIndex !== undefined) {
        // Get main steps (not inside any container)
        const mainSteps = filterStepsInsideSwitchRepeat(currentProcess?.steps || [])
          .sort((a, b) => a.order - b.order);

        // Build the new order with the moved step at target position
        const mainStepIds = mainSteps
          .map(s => s.stepId)
          .filter(id => id && id !== itemId && id !== 'undefined' && id !== 'null');
        mainStepIds.splice(targetMainIndex, 0, itemId);

        // Filter out any undefined values before sending to backend
        const filteredStepIds = mainStepIds.filter(id => id && id !== 'undefined' && id !== 'null');
        if (filteredStepIds.length > 0) {
          console.log('[handleDragInsideOutside] Reordering main steps:', filteredStepIds);
          await graphQlClient.request(StepsGraphQL.mutations.reorderSteps, {
            steps: { stepsIds: filteredStepIds },
          });
        }
      }

      const actionMessage = moveInside
        ? `Step moved inside ${containerStep.name}`
        : `Step moved outside ${containerStep.name}`;

      dispatch(
        appendActionMessage({
          message: actionMessage,
          type: SnackType.SUCCESS,
        }),
      );
    } catch (e: any) {
      console.error('Error in handleDragInsideOutside:', e);
      showErrorSnack(e);
    } finally {
      // Await getProcessData to ensure data is refreshed before setting saving to false
      await getProcessData(processId);
      setSaving(false);
    }
  };

  const handleStepReorder = async (source: number, destination: number, loopId?: string) => {
    if (source === destination) return;
    setSaving(true);
    // Custom logic is needed since reorder in the case of steps may not be sequential if there step loops/switches
    const updatedProcess = { ...currentProcess };
    let updatedProcSteps: Step[] = [];
    if (!updatedProcess.steps) {
      setSaving(false);
      return;
    }

    let stepsIds: string[] = [];

    // This is the case where the steps are inside a loop/switch
    if (loopId) {
      const loopStep = updatedProcess.steps.find((item) => item?.stepId === loopId) as Step;
      const loopedSteps = loopStep?.stepData?.stepsIds?.split(',').filter((id: any) => id && id.trim() && id !== 'undefined' && id !== 'null');
      updatedProcSteps = updatedProcess.steps
        .filter((item) => loopedSteps?.includes(item?.stepId))
        .sort((a, b) => (a.order > b.order ? 1 : -1));
    } else {
      updatedProcSteps = filterStepsInsideSwitchRepeat(
        updatedProcess.steps.sort((a, b) => (a.order > b.order ? 1 : -1)),
      );
    }

    if (!updatedProcess || updatedProcSteps?.length === 0) {
      setSaving(false);
      return;
    }

    const auxSrc = updatedProcSteps.findIndex((item) => item?.stepId === updatedProcSteps[source]?.stepId);
    const auxDest = updatedProcSteps.findIndex((item) => item?.stepId === updatedProcSteps[destination]?.stepId);
    const dest = updatedProcSteps[auxSrc];

    updatedProcSteps.splice(auxSrc, 1);
    updatedProcSteps.splice(auxDest, 0, dest);

    stepsIds = updatedProcSteps
      .map((item: Step) => item?.stepId)
      .filter((id: any) => id && id !== 'undefined' && id !== 'null');

    if (stepsIds.length === 0) {
      setSaving(false);
      return;
    }

    try {
      const data: reorderStepsRequest = await graphQlClient.request(StepsGraphQL.mutations.reorderSteps, {
        steps: { stepsIds },
      });
      if (data?.reorderSteps.messages[0] === 'Update successful') {
        dispatch(
          appendActionMessage({
            message: localeActionMessages['stepsReorderedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      showErrorSnack(e);
    } finally {
      // Await getProcessData to ensure data is refreshed before setting saving to false
      await getProcessData(processId);
      setSaving(false);
    }
  };

  const handleLinkProcessPolicies = async (processId: string, policies: string[]) => {
    try {
      const updateResult: processPolicyLinkRequest = await graphQlClient.request(
        ProcessesGraphQL.mutations.linkProcessPolicy,
        {
          processId,
          policyId: JSON.stringify(policies),
        },
      );
      if (updateResult?.linkProcessPolicy?.processId) {
        await getProcessData(processId);
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleCloneProcess = async () => {
    if (!currentProcess) return;

    try {
      setLoading(true);
      // Generate a unique name for the cloned process
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_');
      const clonedProcessName = `Copy of ${currentProcess.name} ${timestamp}`;

      // First create the new process
      const newProcess = {
        ...currentProcess,
        name: clonedProcessName,
        serviceId: selectedService?.serviceId,
      };

      // Create a new object without the properties we don't want to include
      const processToCreate = {
        name: newProcess.name,
        goal: newProcess.goal,
        category: newProcess.category,
        serviceId: newProcess.serviceId,
      };

      const createResult: createOrUpdateProcessRequest = await graphQlClient.request(
        ProcessesGraphQL.mutations.createProcess,
        {
          process: processToCreate,
        },
      );

      if (createResult?.createProcess?.processId) {
        const newProcessId = createResult.createProcess.processId;

        // Clone all steps
        if (currentProcess.steps) {
          for (const step of currentProcess.steps) {
            const newStep = {
              name: step.name,
              stepType: step.stepType,
              notes: step.notes,
              order: step.order,
              stepData: step.stepData,
              processId: newProcessId,
            };

            await graphQlClient.request(StepsGraphQL.mutations.createStep, {
              step: newStep,
            });
          }
        }

        // Clone all triggers if they exist
        if (currentProcess.triggers) {
          for (const trigger of currentProcess.triggers) {
            const newTrigger = {
              name: trigger.name,
              triggerType: trigger.triggerType,
              triggerData: trigger.triggerData,
              processId: newProcessId,
              processName: clonedProcessName,
            };

            await graphQlClient.request(TriggersGraphQL.mutations.createTrigger, {
              trigger: newTrigger,
            });
          }
        }

        // Link policies if they exist
        if (currentProcess.policies && currentProcess.policies.length > 0) {
          const policyIds = currentProcess.policies.map((policy) => policy.policyId);
          await handleLinkProcessPolicies(newProcessId, policyIds);
        }

        dispatch(
          appendActionMessage({
            message: localeActionMessages['processClonedSuccessfully'] || 'Process cloned successfully',
            type: SnackType.SUCCESS,
          }),
        );

        // Navigate to the new process
        history.push(`/processes/${newProcessId}`);
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProcessUpdate = async (process: Process) => {
    try {
      const query = ProcessesGraphQL.mutations.updateProcess;
      const params = {
        process: {
          ...process,
        },
      };

      delete params.process.service;
      delete params.process.steps;
      delete params.process.triggers;
      delete params.process.policies;
      delete params.process.triggeredBy;
      delete params.process.location;
      delete params.process.discrepancies;
      delete params.process.lastUpdatedAt;
      delete params.process.currentVersion;

      const data: createOrUpdateProcessRequest = await graphQlClient.request(query, params);
      if (data?.createProcess?.processId || data?.updateProcess?.processId) {
        setCurrentProcess((prevCurr) => {
          if (!prevCurr) return prevCurr;
          return {
            ...prevCurr,
            ...process,
          };
        });
        dispatch(
          appendActionMessage({ message: localeActionMessages['processUpdatedSuccessfully'], type: SnackType.SUCCESS }),
        );
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleProcessStatusUpdate = (status: ProcessStatus) => {
    setCurrentProcess((prevCurr) => {
      if (!prevCurr) return prevCurr;
      return {
        ...prevCurr,
        status: status,
      };
    });
  };

  const clearStepData = () => {
    setSelectedStep('');
    setSelectedStepData(undefined);
    setUpdatingStep(undefined);
    setOriginalStepData(undefined);
    setStepName('');
    setLinkedProcess({ processId: '', name: '' });
    setNotes('');
    setCondition('');
    setDescription('');
    setTimingDescription('');
    setTimingName('');
    setTimingType(TimingType.IMMEDIATE);
    setPositionForNewStep(null);
    setNewStepType(null);
  };

  const handleDrawerClose = () => {
    setShowDrawer(false);
  };

  const handleTriggerDrawerClose = () => {
    setShowTriggerDrawer(false);
    // Preserve edit mode if user was editing a step when opening the trigger drawer
    getProcessData(processId, undefined, editMode);
  };


  const handleShowJsonImport = () => {
    setShowJsonImport(true);
  };

  const handleShowJsonExport = () => {
    setShowJsonExport(true);
  };

  const handleCloseJsonImport = () => {
    setShowJsonImport(false);
  };

  const handleCloseJsonExport = () => {
    setShowJsonExport(false);
  };

  const handleDiscrepancyUpdate = async () => {
    getProcessData(processId);

    // Update discrepancy badge count
    try {
      const data: getActiveDiscrepanciesAndPendingCountRequest = await graphQlClient.request(
        DiscrepanciesGraphQL.queries.getActiveDiscrepanciesAndPendingCount,
      );
      if (data?.getActiveDiscrepanciesAndPendingCount) {
        const totalCount =
          data.getActiveDiscrepanciesAndPendingCount.activeDiscrepanciesCount +
          data.getActiveDiscrepanciesAndPendingCount.activePendingCount;
        dispatch(setDiscrepancyCount(totalCount));
      }
    } catch (error) {
      console.error('Error refreshing discrepancy count:', error);
    }
  };

  const updateOrganizationExecutionRequested = async () => {
    try {
      const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
        OrganizationsGraphQL.mutations.updateOrganization,
        {
          organization: {
            organizationId: organizationId || '',
            onboardingDetails: {
              ...onboardingDetails,
              executionRequested: true,
            },
          },
        },
      );
      if (data?.updateOrganization?.organizationId) {
        dispatch(setOrganizationData(data.updateOrganization));
      }
    } catch (error) {
      console.error('Failed to update execution requested status:', error);
    }
  };

  // Request execution handlers
  const handleRequestExecution = async () => {
    setShowRequestExecutionModal(true);
    setLoadingUsers(true);

    try {
      const data: getAssignableUsersRequest = await graphQlClient.request(
        UserGraphQL.queries.getAssignableUsers,
      );
      setOrganizationUsers(data?.getAssignableUsers || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleConfirmRequestExecution = async (data: RequestExecutionConfirmData) => {
    if (!currentProcess) return;

    setSubmittingRequest(true);

    try {
      await graphQlClient.request(ProcessExecutionGraphQL.mutations.assignProcessExecution, {
        input: {
          processId: processId,
          processSnapshot: {
            name: currentProcess.name,
            goal: currentProcess.goal,
            category: currentProcess.category,
          },
          assigneeId: data.assigneeId,
          assignerNote: data.assignerNote,
        },
      });

      if (!onboardingDetails?.executionRequested) {
        updateOrganizationExecutionRequested();
      }

      dispatch(
        appendActionMessage({
          message: localeProcesses['executionRequestedSuccessfully'],
          type: SnackType.SUCCESS,
        }),
      );

      setShowRequestExecutionModal(false);
    } catch (error: any) {
      console.error('Error requesting execution:', error);
      dispatch(
        appendActionMessage({
          message: error?.response?.errors?.[0]?.message || localeProcesses['errorRequestingExecution'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setSubmittingRequest(false);
    }
  };
  return (
    <>
      <Box className="flex h-screen w-full -m-8" sx={{ margin: '-24px' }}>
        {processNotFound ? (
          <Box className="flex items-center justify-center w-full h-full">
            <Box className="text-center">
              <Typography variant="h5" className="mb-2">
                {localeProcesses['processNotFound']}
              </Typography>
              <Typography variant="body16" className="text-gray-600">
                {localeProcesses['processNotFoundMessage']}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box className="flex w-full h-full">
            <Box className="w-96 py-6 min-w-96 bg-white h-full overflow-y-auto" data-tutorial-id="process-sidebar">
            <Box className="px-4">
              <ProcessSidebar
                origProcess={currentProcess}
                editMode={editMode}
                title={selectedArea?.name || ''}
                titleLink={'/services?areaId=' + selectedArea?.areaId}
                subTitle={selectedService?.name || ''}
                subTitleLink={'/processes?serviceId=' + selectedService?.serviceId}
                goal={currentProcess?.goal || ''}
                category={currentProcess?.category || ''}
                generatedFromName={currentProcess?.generatedFromName || ''}
                policies={currentProcess?.policies || []}
                triggers={currentProcess?.triggers || []}
                triggeredBy={currentProcess?.triggeredBy || []}
                handleSelectStep={handleSelectStep}
                handleEditStep={handleEditStep}
                handleNewStep={addNewStep}
                handleEditTriggers={toggleEditTriggers}
                selectedStep={selectedStep}
                steps={currentProcess?.steps || []}
                dragEndHandler={handleStepReorder}
                dragInsideOutsideHandler={handleDragInsideOutside}
                handleProcessUpdate={handleProcessUpdate}
                handleLinkProcessPolicies={handleLinkProcessPolicies}
                saving={saving}
                isExecuting={isExecuting}
                disableAddStep={isInProgressStep(selectedStep) && currentProcess?.steps?.filter(s => !isInProgressStep(s.stepId)).length === 0}
                stepWithUnsavedChanges={hasUnsavedChanges() ? selectedStep : undefined}
                editingStepTitle={editMode ? updatingStep?.name : undefined}
              />
            </Box>
          </Box>
          <Box className="flex-1 h-full flex flex-col bg-backgroundUI">
            <Box className="sticky top-0 z-10 bg-backgroundUI">
              <ProcessHeader
                ref={processHeaderRef}
                mode={editMode ? 'edit' : 'view'}
                processId={processId}
                title={currentProcess?.name || ''}
                discrepancies={currentProcess?.discrepancies || []}
                onDiscrepancyUpdate={handleDiscrepancyUpdate}
                onSave={handleSave}
                onCancel={handleCancel}
                disableCancel={isInProgressStep(selectedStep) && currentProcess?.steps?.filter(s => !isInProgressStep(s.stepId)).length === 0}
                onDelete={handleDelete}
                onProcessDelete={() => handleDeleteProcess(processId)}
                onEdit={toggleEdit}
                saving={saving}
                editMode={editMode}
                location={currentProcess?.location || ''}
                showRemove={!isInProgressStep(selectedStep)}
                handleShowJsonImport={handleShowJsonImport}
                handleShowJsonExport={handleShowJsonExport}
                handleProcessUpdate={handleProcessUpdate}
                handleProcessStatusUpdate={handleProcessStatusUpdate}
                handleCloneProcess={handleCloneProcess}
                origProcess={currentProcess}
                onToggleExecutionsTable={() => setShowExecutionsTable(!showExecutionsTable)}
                showExecutionsTable={showExecutionsTable}
                isExecuting={isExecuting}
                onStartExecution={checkAndShowExecutionConfirm}
                onCancelExecution={handleCancelExecution}
                onRequestExecution={handleRequestExecution}
              />
            </Box>
            <Box className="flex-1 overflow-y-auto" data-tutorial-id="process-content">
            {editMode ? (
              <ProcessContent
                key={selectedStep}
                ref={processContentRef}
                parentCategory={currentProcess?.category || ''}
                newStepType={stepType || newStepType}
                // TODO: Refactor all initial data
                initialData={selectedStepData}
                initialStepName={stepName}
                initialLinkedProcess={linkedProcess}
                initialNotes={notes}
                initialCondition={condition}
                initialDescription={description}
                initialTimingDescription={timingDescription}
                initialTimingName={timingName}
                initialTimingType={timingType}
                updatingStep={handleStepDetailChange}
                isConditionStep={
                  (creationContext?.id &&
                    currentProcess?.steps?.find((step) => step.stepId === creationContext.id)?.stepType ===
                      StepType.SWITCH) ||
                  false
                }
              />
            ) : (
              <ProcessContentStatic
                delay={debounceSeparation}
                process={currentProcess}
                stepsRefs={stepsRefs}
                auxStep={currentProcess?.steps?.filter((step) => step?.stepId === selectedStep)[0]}
                handleSelectStep={handleSelectStep}
                handleEditStep={handleEditStep}
                isExecuting={isExecuting}
                stepExecutions={stepExecutions}
                currentStepIndex={currentStepIndex}
                onMarkStep={handleMarkStep}
                onGoBack={handleGoBack}
                isGoingBack={isGoingBack}
                hasVersions={!!currentProcess?.currentVersion}
                // Substep execution props - keyed by parentStepId (step definition ID)
                substepExecutions={substepExecutions}
                currentSubstepIndices={currentSubstepIndices}
                repeatIterations={repeatIterations}
                completedIterations={completedIterations}
                showIterationCompleteFor={showIterationCompleteFor}
                onMarkSubstep={handleMarkSubstep}
                onRepeatAddAnother={handleRepeatAddAnother}
                onRepeatDone={handleRepeatDone}
                executionNote={assignerNoteContent}
              />
            )}
            {/* Version History Section */}
            {!editMode && currentProcess?.currentVersion && (
              <Box id="version-history" className={`px-8 py-6 ${wolfieEnabled ? 'mb-28' : ''}`}>
                <Box className="max-w-screen-md mx-auto px-4">
                  <ProcessVersionHistory
                    processId={processId}
                    currentVersion={currentProcess.currentVersion}
                    lastUpdatedAt={currentProcess.lastUpdatedAt}
                  />
                </Box>
              </Box>
            )}
            </Box>
          </Box>
        </Box>
        )}
      </Box>
      <CustomDrawer title={localeProcesses['edit']} isOpen={showDrawer} onClose={handleDrawerClose} size="large">
        <AddProcessForm
          parentService={{ name: selectedService?.name || '', serviceId: selectedService?.serviceId || '' }}
          onClose={handleDrawerClose}
          handleSave={handleDrawerSave}
          handleDelete={handleDeleteProcess}
          drawerType={DrawerType.EDIT}
          initialData={currentProcess}
          saveBtnText={localeProcesses['editProcess']}
          saving={saving}
        />
      </CustomDrawer>
      <CustomDrawer
        title={localeProcesses['editTriggers']}
        isOpen={showTriggerDrawer}
        onClose={handleTriggerDrawerClose}
        size="large">
        <AddTriggerForm onClose={handleTriggerDrawerClose} initialData={currentProcess} />
      </CustomDrawer>
      {/* {showConfirmLeaveModal && (
        <CustomModal
          isOpen={!!showConfirmLeaveModal}
          title={localeCommon['confirm']}
          customConfirmColor="error"
          customCancelText={localeCommon['backToEditing']}
          customConfirmText={localeCommon['discardChanges']}
          maxWidth="600px"
          showButtons={true}
          onClose={closeConfirmLeaveModal}
          onConfirm={closeAndConfirmLeaveModal}>
          <ConfirmLeaveModal />
        </CustomModal>
      )} */}
      {/* Add Step / Select Step While Editing Confirmation Modal */}
      {showAddStepConfirmDialog && (() => {
        const isFirstStepOnly = isInProgressStep(selectedStep) &&
          currentProcess?.steps?.filter(s => !isInProgressStep(s.stepId)).length === 0;
        const isSelectingStep = !!pendingStepSelection;
        return (
          <CustomModal
            isOpen={showAddStepConfirmDialog}
            title={isFirstStepOnly
              ? (localeProcesses['firstStepRequired'] || 'A process must have at least one step, please save it')
              : (localeProcessForm['unsavedStepChanges'] || 'Unsaved Changes')}
            maxWidth="600px"
            showButtons={false}
            hideClose={isFirstStepOnly}
            onClose={handleContinueEditing}>
            <Box className="text-center space-y-4">
              {!isFirstStepOnly && (
                <Typography variant="body1">
                  {localeProcessForm['unsavedStepChangesMessage'] || 'You have unsaved changes in the current step. What would you like to do?'}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, pt: 2 }}>
                <WolfButton
                  variant="contained"
                  color="primary"
                  sx={{ minWidth: '250px' }}
                  onClick={handleSaveAndAddNew}>
                  {isSelectingStep
                    ? (localeProcessForm['saveAndSelect'] || 'Save & Select')
                    : (localeProcessForm['saveAndAddNew'] || 'Save & Add New')}
                </WolfButton>
                {/* Hide discard and continue options if editing the only step (first step must be saved) */}
                {!isFirstStepOnly && (
                  <>
                    <WolfButton
                      variant="outlined"
                      color="error"
                      sx={{ minWidth: '250px' }}
                      onClick={handleDiscardAndAddNew}>
                      {isSelectingStep
                        ? (localeProcessForm['discardAndSelect'] || 'Discard & Select')
                        : (localeProcessForm['discardAndAddNew'] || 'Discard changes and add new')}
                    </WolfButton>
                    <WolfButton
                      variant="outlined"
                      color="secondary"
                      sx={{ minWidth: '250px' }}
                      onClick={handleContinueEditing}>
                      {localeProcessForm['continueEditing'] || 'Continue Editing'}
                    </WolfButton>
                  </>
                )}
              </Box>
            </Box>
          </CustomModal>
        );
      })()}
      {/* Continue Execution Confirmation Modal */}
      {showContinueExecutionModal && (
        <CustomModal
          isOpen={showContinueExecutionModal}
          title={localeProcesses['confirmContinueExecution']}
          maxWidth="600px"
          showButtons={false}
          onClose={() => {
            setShowContinueExecutionModal(false);
            setPendingExecutionStartDate(null);
            setPendingExecutionId(null);
            setAssignerNoteContent(null);
          }}>
          <Box className="text-center space-y-4">
            <Typography variant="body1">
              {localeProcesses['confirmContinueExecutionMessage']}{' '}
              {pendingExecutionStartDate && DateTime.fromISO(pendingExecutionStartDate).toFormat('dd-MM-yyyy HH:mm')}.
            </Typography>
            <Typography variant="body1">
              {localeProcesses['continueOrStartNew']}
            </Typography>
            <Box className="flex justify-center gap-6 pt-4">
              <Box className="flex items-center gap-1">
                <WolfButton
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setShowContinueExecutionModal(false);
                    setPendingExecutionStartDate(null);
                    setAssignerNoteContent(null);
                    handleStartExecution(true); // Start fresh
                  }}>
                  {localeProcesses['startOver']}
                </WolfButton>
                <Tooltip title={localeProcesses['startOverTooltip']} arrow>
                  <InfoOutlined fontSize="small" className="text-gray-400 cursor-help" />
                </Tooltip>
              </Box>
              <WolfButton
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setShowContinueExecutionModal(false);
                  setPendingExecutionStartDate(null);
                  setPendingExecutionId(null);
                  setAssignerNoteContent(null);
                }}>
                {localeCommon['cancel']}
              </WolfButton>
              <WolfButton
                variant="contained"
                color="primary"
                onClick={() => {
                  setShowContinueExecutionModal(false);
                  setPendingExecutionStartDate(null);
                  setPendingExecutionId(null);
                  // Show assigner note if exists, otherwise continue directly
                  if (assignerNoteContent) {
                    setNoteModalNextAction('start');
                    setShowAssignerNoteModal(true);
                  } else {
                    handleStartExecution(); // Continue existing
                  }
                }}>
                {localeProcesses['continueExecution']}
              </WolfButton>
            </Box>
          </Box>
        </CustomModal>
      )}
      {/* Assigner Note Modal */}
      {showAssignerNoteModal && (
        <CustomModal
          isOpen={showAssignerNoteModal}
          title={localeProcesses['noteFromAssigner']}
          maxWidth="600px"
          showButtons={false}
          onClose={() => {
            setShowAssignerNoteModal(false);
            setAssignerNoteContent(null);
            setNoteModalNextAction('start');
          }}>
          <Box className="text-center space-y-4">
            <Typography variant="body1" className="whitespace-pre-wrap text-left">
              {assignerNoteContent}
            </Typography>
            <Box className="flex justify-center gap-4 pt-4">
              <WolfButton
                variant="contained"
                color="primary"
                onClick={() => {
                  setShowAssignerNoteModal(false);
                  // Don't clear assignerNoteContent - keep it to show above first step
                  if (noteModalNextAction === 'showContinueModal') {
                    setShowContinueExecutionModal(true);
                  } else {
                    handleStartExecution();
                  }
                  setNoteModalNextAction('start');
                }}>
                {localeCommon['accept']}
              </WolfButton>
            </Box>
          </Box>
        </CustomModal>
      )}
      {showDeleteProcessModal && (
        <CustomModal
          isOpen={showDeleteProcessModal}
          customClass="!overflow-hidden"
          maxWidth="600px"
          title={localeCommon['deleteProcess']}
          showButtons={true}
          customCancelText={localeCommon['cancel']}
          customConfirmText={localeCommon['yesDelete']}
          customConfirmColor="error"
          onClose={cancelDeleteProcess}
          onConfirm={() => confirmDeleteProcess()}>
          <Box className="text-center space-y-4">
            <Typography
              variant="body1"
              dangerouslySetInnerHTML={{ __html: localeCommon['deleteProcessConfirmation'] }}
            />
            <Typography variant="body1">
              {localeCommon['deleteProcessQuestion']}
            </Typography>
          </Box>
        </CustomModal>
      )}
      <ImportExportProcessHandler
        processId={processId}
        steps={currentProcess?.steps || []}
        showJsonImport={showJsonImport}
        showJsonExport={showJsonExport}
        handleCloseJsonImport={handleCloseJsonImport}
        handleCloseJsonExport={handleCloseJsonExport}
        refresh={getProcessData}
      />
      {showExecutionsTable && (
        <CustomModal
          isOpen={showExecutionsTable}
          maxHeight="80vh"
          customClass="!overflow-hidden"
          maxWidth="1024px"
          title={localeProcesses['executionHistory']}
          showButtons={false}
          onClose={() => setShowExecutionsTable(false)}>
          <Box className="h-full">
            <ProcessExecutionsTable key={`executions-${processId}-${showExecutionsTable}`} processId={processId} modalMode={true}/>
          </Box>
        </CustomModal>
      )}

      {/* Execution Status Selection Modal */}
      <CustomModal
        isOpen={showStatusModal}
        title={localeProcesses['selectExecutionStatus']}
        onClose={() => setShowStatusModal(false)}
        maxWidth="600px"
        showButtons={false}>
        <Box className="flex flex-col gap-4 py-4">
          <Typography variant="body2" className="text-gray-600">
            {localeProcesses['selectExecutionStatusMessage']}
          </Typography>

          <Box className="flex flex-col gap-2">
            <WolfButton
              onClick={async () => {
                setShowStatusModal(false);
                await handleCompleteExecution(ProcessExecutionStatus.COMPLETED);
              }}
              variant="contained"
              color="success"
              fullWidth>
              {localeProcesses['markAsCompleted']}
            </WolfButton>
            <WolfButton
              onClick={async () => {
                setShowStatusModal(false);
                await handleCompleteExecution(ProcessExecutionStatus.FAILED);
              }}
              variant="contained"
              color="error"
              fullWidth>
              {localeProcesses['markAsFailed']}
            </WolfButton>
            <WolfButton
              onClick={async () => {
                setShowStatusModal(false);
                await handleCompleteExecution(ProcessExecutionStatus.CANCELLED);
              }}
              variant="outlined"
              color="tertiary"
              fullWidth>
              {localeProcesses['markAsCancelled']}
            </WolfButton>
          </Box>
        </Box>
      </CustomModal>

      {/* Switch Organization Modal */}
      <CustomModal
        isOpen={showSwitchOrgModal}
        title={localeProcesses['processFoundInOtherOrg'].replace(
          '{organizationName}',
          crossOrgResult?.organizationName || '',
        )}
        onClose={() => setShowSwitchOrgModal(false)}
        maxWidth="600px"
        showButtons={true}
        customConfirmText={localeProcesses['switchToOrganization']}
        customCancelText={localeCommon['cancel']}
        onConfirm={async () => {
          if (crossOrgResult) {
            try {
              setLoading(true);
              await switchToProcessOrganization({
                process: {} as any,
                organizationId: crossOrgResult.organizationId,
                organizationName: crossOrgResult.organizationName,
                userRole: '',
              });

              dispatch(
                appendActionMessage({
                  message: `Switched to organization: ${crossOrgResult.organizationName}`,
                  type: SnackType.SUCCESS,
                }),
              );

              setShowSwitchOrgModal(false);

              // Reload process data with the new organization context
              await getProcessData(processId);
            } catch (err) {
              console.error('Failed to switch organization:', err);
              dispatch(
                appendActionMessage({
                  message: localeActionMessages['errorSwitchingOrganization'] || localeCommon['error'],
                  type: SnackType.ERROR,
                }),
              );
              setShowSwitchOrgModal(false);
              setLoading(false);
            }
          }
        }}>
        <SwitchOrganizationModal organizationName={crossOrgResult?.organizationName || ''} />
      </CustomModal>

      {/* Request Execution Modal */}
      <RequestExecutionModal
        isOpen={showRequestExecutionModal}
        onClose={() => setShowRequestExecutionModal(false)}
        onConfirm={handleConfirmRequestExecution}
        users={organizationUsers}
        loading={loadingUsers}
        submitting={submittingRequest}
      />
    </>
  );
};

export default ProcessDescription;
